import { useEffect, useState } from "react";
import {
  PublicProductStats,
  shareableLinksService,
} from "../services/shareableLinksService";
import type { FactorEntry } from "../services/productFactorsService";

export function publicPortfolioFactorKey(providerId: number, productId: number): string {
  return `${providerId}:${productId}`;
}

export interface UsePublicProductStatsResult {
  stats: PublicProductStats | null;
  providerNames: Record<number, string>;
  portfolioFactors: Record<string, FactorEntry[]>;
  isLoading: boolean;
  error: string | null;
}

export function usePublicProductStats(): UsePublicProductStatsResult {
  const [stats, setStats] = useState<PublicProductStats | null>(null);
  const [providerNames, setProviderNames] = useState<Record<number, string>>({});
  const [portfolioFactors, setPortfolioFactors] = useState<Record<string, FactorEntry[]>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setIsLoading(true);
      setError(null);

      try {
        const nextStats = await shareableLinksService.getPublicProductStats();
        if (cancelled) return;
        setStats(nextStats);

        const providerIds = nextStats.topPortfolios
          .map((portfolio) => portfolio.providerId)
          .filter((providerId): providerId is number => providerId !== null);

        if (providerIds.length > 0) {
          try {
            const providers = await shareableLinksService.getPublicProviders(providerIds);
            if (cancelled) return;
            setProviderNames(
              Object.fromEntries(providers.map((provider) => [provider.id, provider.name]))
            );
          } catch {
            if (cancelled) return;
            setProviderNames({});
          }
        } else {
          setProviderNames({});
        }

        const factorEntries = await Promise.all(
          nextStats.topPortfolios.slice(0, 4).map(async (portfolio) => {
            if (portfolio.providerId === null) return null;
            try {
              const response = await shareableLinksService.getPublicFactorsPage(
                portfolio.providerId,
                "portfolio",
                portfolio.productId,
                1
              );
              return [
                publicPortfolioFactorKey(portfolio.providerId, portfolio.productId),
                response.items,
              ] as const;
            } catch {
              return null;
            }
          })
        );
        if (cancelled) return;
        setPortfolioFactors(Object.fromEntries(factorEntries.filter((entry) => entry !== null)));
      } catch {
        if (cancelled) return;
        setStats(null);
        setProviderNames({});
        setPortfolioFactors({});
        setError("Failed to load the public product catalog.");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  return { stats, providerNames, portfolioFactors, isLoading, error };
}
